import type { FormEvent } from 'react';
import { useState } from 'react';
import { faPlus } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { DialogClose } from '@radix-ui/react-dialog';

import { AddressField } from '@/components/billing/address-field';
import { PaymentField } from '@/components/billing/payment-field';
import { StripeElementsProvider } from '@/components/billing/stripe-elements-provider';
import { StandardAlert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Label } from '@/components/ui/label';
import { Heading5, MutedText } from '@/components/ui/typography';
import { useStripePaymentMethod } from '@/hooks/billing/use-create-payment-method';
import http, { extractApiErrorMessage } from '@/lib/http';
import { cmsBillingPaymentMethodsPath } from '@/lib/routes';

interface AddCardDialogProps {
  canAddPaymentMethod: boolean;
  stripePublicKey: string;
  /** Called with the newly created Stripe payment method id right after the dialog closes, so
   * the caller can refresh its own data and/or select the new card - this component has no
   * opinion on that, since it's used both on the billing page and inside other modals. */
  onAdded?: (paymentMethodId: string) => void;
}

function AddPaymentMethodForm({ onSuccess }: { onSuccess: (paymentMethodId: string) => void }) {
  const { createPaymentMethod } = useStripePaymentMethod();
  const [setDefault, setSetDefault] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setProcessing(true);
    setError(null);

    try {
      const paymentMethodId = await createPaymentMethod();
      await http.post(cmsBillingPaymentMethodsPath({ format: 'json' }), {
        paymentMethod: { id: paymentMethodId, setDefault },
      });
      onSuccess(paymentMethodId);
    } catch (submissionError) {
      setError(
        extractApiErrorMessage(submissionError) ??
          (submissionError instanceof Error
            ? submissionError.message
            : 'Something went wrong. Please try again.'),
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="space-y-3">
        <Heading5>Payment Details</Heading5>
        <PaymentField processing={processing} />
      </div>

      <div className="space-y-3">
        <Heading5>Billing Address</Heading5>
        <AddressField />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="make-default"
          checked={setDefault}
          onCheckedChange={(checked) => setSetDefault(checked === true)}
          disabled={processing}
        />
        <Label htmlFor="make-default" className="cursor-pointer">
          Set this as my default payment method
        </Label>
      </div>

      {error ? <StandardAlert variant="destructive" description={error} /> : null}

      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline" disabled={processing}>
            Go back
          </Button>
        </DialogClose>
        <Button isLoading={processing} type="submit">
          {processing ? 'Saving...' : 'Save Card'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function AddPaymentMethodDialog({
  canAddPaymentMethod,
  stripePublicKey,
  onAdded,
}: AddCardDialogProps) {
  const [open, setOpen] = useState(false);

  const handleAdded = (paymentMethodId: string) => {
    setOpen(false);
    onAdded?.(paymentMethodId);
  };

  return (
    <div className="space-y-2">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="link" className="w-fit gap-1.5" disabled={!canAddPaymentMethod}>
            <Icon icon={faPlus} />
            Add payment method
          </Button>
        </DialogTrigger>

        <DialogContent size="md" dismissible={false}>
          <DialogHeader>
            <DialogTitle>Add New Card</DialogTitle>
            <DialogDescription>All transactions are secure and encrypted.</DialogDescription>
          </DialogHeader>

          {open ? (
            <StripeElementsProvider stripePublicKey={stripePublicKey}>
              <AddPaymentMethodForm onSuccess={handleAdded} />
            </StripeElementsProvider>
          ) : null}
        </DialogContent>
      </Dialog>

      {!canAddPaymentMethod && (
        <MutedText>You've reached the maximum number of payment methods.</MutedText>
      )}
    </div>
  );
}
